import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Send, MessageCircle, HelpCircle, Minimize2, MapPin, Navigation } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Floating RonRo Chat Widget with Yardage Mode
 * - Appears in the bottom-right corner above the nav
 * - Expands to a mini chat interface (not full page)
 * - Yardage Mode: GPS-based distance to green
 * - Can be minimized or dismissed
 */
export const RonRoHelpButton = ({ user, activeRound: propActiveRound }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isYardageMode, setIsYardageMode] = useState(false);
  const [ronroData, setRonroData] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [activeRound, setActiveRound] = useState(propActiveRound || null);
  
  // Yardage state
  const [yardageData, setYardageData] = useState(null);
  const [isGettingYardage, setIsGettingYardage] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Check for active round in localStorage (set by scoring/game pages)
  useEffect(() => {
    const checkActiveRound = () => {
      const storedRound = localStorage.getItem('par_seeker_active_round');
      if (storedRound) {
        try {
          const parsed = JSON.parse(storedRound);
          // Check if round is still valid (not older than 12 hours)
          const roundTime = new Date(parsed.started_at || parsed.created_at);
          const now = new Date();
          const hoursSinceStart = (now - roundTime) / (1000 * 60 * 60);
          if (hoursSinceStart < 12) {
            setActiveRound(parsed);
          } else {
            localStorage.removeItem('par_seeker_active_round');
          }
        } catch (e) {
          console.log('Invalid active round data');
        }
      }
    };
    
    checkActiveRound();
    
    // Listen for storage changes (when user starts a round in another tab/component)
    const handleStorage = (e) => {
      if (e.key === 'par_seeker_active_round') {
        checkActiveRound();
      }
    };
    window.addEventListener('storage', handleStorage);
    
    // Also poll periodically in case of same-tab updates
    const interval = setInterval(checkActiveRound, 5000);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);
  
  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);
  
  // Initialize RonRo data
  useEffect(() => {
    if (!user) return;
    
    const isHidden = localStorage.getItem('ronro_help_hidden') === 'true';
    if (isHidden) {
      setIsVisible(false);
      return;
    }
    
    const initRonRo = async () => {
      try {
        const ronroResp = await axios.get(`${API}/ronro/user-id`);
        if (ronroResp.data.exists) {
          setRonroData(ronroResp.data);
          setIsVisible(true);
          // Ensure RonRo is buddy (silent)
          axios.post(`${API}/ronro/ensure-buddy`).catch(() => {});
        }
      } catch (error) {
        console.log('RonRo not available');
      }
    };
    
    initRonRo();
    
    // Show tooltip for new users
    const hasSeenTooltip = localStorage.getItem('ronro_tooltip_seen');
    if (!hasSeenTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(true);
        setTimeout(() => {
          setShowTooltip(false);
          localStorage.setItem('ronro_tooltip_seen', 'true');
        }, 5000);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);
  
  // Load conversation and messages when chat opens
  useEffect(() => {
    if (!isChatOpen || !ronroData) return;
    
    const loadConversation = async () => {
      setIsLoading(true);
      try {
        // Get or create conversation
        const convResp = await axios.get(`${API}/ronro/conversation`);
        const convId = convResp.data.conversation_id;
        setConversationId(convId);
        
        // Load messages
        const msgResp = await axios.get(`${API}/conversations/${convId}/messages`);
        setMessages(msgResp.data || []);
        scrollToBottom();
      } catch (error) {
        console.error('Failed to load RonRo conversation:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadConversation();
  }, [isChatOpen, ronroData, scrollToBottom]);
  
  // Poll for new messages when chat is open
  useEffect(() => {
    if (!isChatOpen || !conversationId) return;
    
    const pollMessages = async () => {
      try {
        const msgResp = await axios.get(`${API}/conversations/${conversationId}/messages`);
        const newMsgs = msgResp.data || [];
        if (newMsgs.length !== messages.length) {
          setMessages(newMsgs);
          scrollToBottom();
        }
      } catch (error) {
        // Silent fail
      }
    };
    
    const interval = setInterval(pollMessages, 3000);
    return () => clearInterval(interval);
  }, [isChatOpen, conversationId, messages.length, scrollToBottom]);
  
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || isSending || !conversationId) return;
    
    const messageText = newMessage.trim();
    setNewMessage('');
    setIsSending(true);
    
    // Optimistically add user message
    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      content: messageText,
      sender_id: user?.user_id,
      sender_name: user?.name || 'You',
      created_at: new Date().toISOString(),
      is_temp: true
    };
    setMessages(prev => [...prev, tempUserMsg]);
    scrollToBottom();
    
    try {
      // Send to RonRo
      const response = await axios.post(`${API}/ronro/chat`, {
        conversation_id: conversationId,
        message: messageText
      });
      
      // Refresh messages to get both user message and RonRo response
      const msgResp = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(msgResp.data || []);
      scrollToBottom();
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.message_id !== tempUserMsg.message_id));
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };
  
  const handleButtonClick = () => {
    if (isChatOpen) {
      // Already in chat, do nothing (use X to close)
      return;
    }
    if (isExpanded) {
      // Open chat from expanded intro card
      setIsChatOpen(true);
      setIsExpanded(false);
    } else {
      // Show intro card first
      setIsExpanded(true);
      setShowTooltip(false);
      localStorage.setItem('ronro_tooltip_seen', 'true');
    }
  };
  
  const handleOpenChat = () => {
    setIsChatOpen(true);
    setIsExpanded(false);
    setTimeout(() => inputRef.current?.focus(), 300);
  };
  
  const handleClose = () => {
    setIsChatOpen(false);
    setIsExpanded(false);
  };
  
  const handleDismiss = () => {
    setIsVisible(false);
    setIsChatOpen(false);
    setIsExpanded(false);
    localStorage.setItem('ronro_help_hidden', 'true');
  };
  
  const handleMinimize = () => {
    setIsChatOpen(false);
    setIsExpanded(false);
    setIsYardageMode(false);
  };
  
  // ============ YARDAGE MODE FUNCTIONS ============
  
  const [locationPermission, setLocationPermission] = useState('unknown'); // 'unknown', 'prompt', 'granted', 'denied'
  const [isMarkingGreen, setIsMarkingGreen] = useState(false);
  const [teeLocation, setTeeLocation] = useState(null); // Stores {lat, lng, hole} when user starts a hole
  const [lastHole, setLastHole] = useState(null); // Track which hole we recorded tee for
  
  // Check location permission status on mount
  useEffect(() => {
    // Check if geolocation is even available
    if (!navigator.geolocation) {
      console.log('[RonRo] Geolocation not supported by browser');
      setLocationPermission('denied');
      return;
    }
    
    // Check permissions API if available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        console.log('[RonRo] Permission status:', result.state);
        setLocationPermission(result.state);
        result.onchange = () => {
          console.log('[RonRo] Permission changed to:', result.state);
          setLocationPermission(result.state);
        };
      }).catch((err) => {
        console.log('[RonRo] Permissions API error:', err);
        setLocationPermission('unknown');
      });
    } else {
      console.log('[RonRo] Permissions API not available');
      setLocationPermission('unknown');
    }
  }, []);
  
  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        console.error('[RonRo] Geolocation not supported');
        reject(new Error('Geolocation not supported by your browser'));
        return;
      }
      
      console.log('[RonRo] Requesting geolocation...');
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('[RonRo] Got position:', position.coords.latitude, position.coords.longitude);
          resolve(position);
        },
        (error) => {
          console.error('[RonRo] Geolocation error:', error.code, error.message);
          reject(error);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 20000,  // 20 second timeout
          maximumAge: 0 
        }
      );
    });
  };

  const getYardage = async () => {
    if (!activeRound?.course_id) {
      setGpsError('Start a round to use yardage mode');
      return;
    }
    
    setIsGettingYardage(true);
    setGpsError(null);
    
    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;
      setLocationPermission('granted');
      
      const currentHole = activeRound.current_hole || 1;
      
      // Check if this is a new hole - if so, record current position as tee
      let teeLat = null;
      let teeLng = null;
      
      if (lastHole !== currentHole) {
        // New hole - this is the tee location
        setTeeLocation({ lat: latitude, lng: longitude, hole: currentHole });
        setLastHole(currentHole);
        // Don't send tee location on first request (user is AT the tee)
      } else if (teeLocation && teeLocation.hole === currentHole) {
        // Same hole, user moved - send tee location for calculation
        teeLat = teeLocation.lat;
        teeLng = teeLocation.lng;
      }
      
      const response = await axios.post(`${API}/yardage/calculate`, {
        course_id: activeRound.course_id,
        lat: latitude,
        lng: longitude,
        hole: currentHole,
        tee_lat: teeLat,
        tee_lng: teeLng
      });
      
      setYardageData(response.data);
      
    } catch (error) {
      console.error('Yardage error:', error);
      if (error.code === 1) {
        // PERMISSION_DENIED
        setLocationPermission('denied');
        setGpsError('Location permission denied. Please enable location access in your browser or device settings.');
      } else if (error.code === 2) {
        // POSITION_UNAVAILABLE
        setGpsError('Unable to get location. Please check that GPS/Location Services is enabled on your device.');
      } else if (error.code === 3) {
        // TIMEOUT
        setGpsError('Location request timed out. This may happen indoors. Try moving outside or near a window.');
      } else if (error.message?.includes('not supported')) {
        setGpsError('Location is not supported on this browser. Try using Safari or Chrome.');
      } else {
        setGpsError(error.response?.data?.message || error.message || 'Unable to get location. Please try again.');
      }
    } finally {
      setIsGettingYardage(false);
    }
  };
  
  const handleHoleCorrection = async (newHole) => {
    if (!activeRound?.course_id || !yardageData) return;
    
    try {
      const position = await getCurrentPosition();
      
      // Record correction for learning
      await axios.post(`${API}/yardage/correct-hole`, {
        course_id: activeRound.course_id,
        from_hole: yardageData.hole,
        to_hole: newHole,
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      
      // Recalculate with corrected hole
      const response = await axios.post(`${API}/yardage/calculate`, {
        course_id: activeRound.course_id,
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        hole: newHole
      });
      
      setYardageData(response.data);
    } catch (error) {
      console.error('Hole correction error:', error);
    }
  };
  
  // Mark current location as green center for the current hole
  const markGreenLocation = async () => {
    if (!activeRound?.course_id || !yardageData?.hole) {
      toast.error('Unable to mark location');
      return;
    }
    
    setIsMarkingGreen(true);
    
    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;
      
      // Send green location to backend
      const response = await axios.post(`${API}/yardage/mark-green`, {
        course_id: activeRound.course_id,
        hole: yardageData.hole,
        lat: latitude,
        lng: longitude
      });
      
      if (response.data.success) {
        toast.success(`Green location saved for Hole ${yardageData.hole}!`);
        
        // Refresh yardage data - should now use GPS
        await getYardage();
      } else {
        toast.error(response.data.message || 'Failed to save location');
      }
    } catch (error) {
      console.error('Mark green error:', error);
      toast.error('Failed to mark green location');
    } finally {
      setIsMarkingGreen(false);
    }
  };
  
  const openYardageMode = () => {
    setIsYardageMode(true);
    setIsExpanded(false);
    setIsChatOpen(false);
    getYardage();
  };
  
  // ============ END YARDAGE MODE ============
  
  if (!isVisible || !user) return null;
  
  const isRonroMessage = (msg) => {
    return msg.sender_id === ronroData?.user_id || msg.is_ronro_response;
  };
  
  return (
    <>
      {/* Floating Container */}
      <div
        className="fixed z-[100] transition-all duration-300 ease-out"
        style={{
          bottom: 'calc(120px + env(safe-area-inset-bottom))',
          right: '16px',
          maxWidth: 'calc(100vw - 32px)'
        }}
        data-testid="ronro-help-container"
      >
        {/* Tooltip */}
        {showTooltip && !isExpanded && !isChatOpen && (
          <div 
            className="absolute bottom-full right-0 mb-2 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl p-3 shadow-lg animate-bounce-subtle"
            style={{ minWidth: '180px' }}
          >
            <p className="text-sm text-[var(--ps-text-primary)] font-medium">
              Need help? Chat with RonRo! 🦅
            </p>
            <p className="text-xs text-[var(--ps-text-muted)] mt-1">
              Your personal golf assistant
            </p>
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-[var(--ps-bg-surface)] border-r border-b border-[var(--ps-border)] transform rotate-45" />
          </div>
        )}
        
        {/* Chat Window */}
        {isChatOpen && (
          <div 
            className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
            style={{ width: '340px', maxHeight: '450px' }}
            data-testid="ronro-chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="w-9 h-9 ring-2 ring-white/30">
                  <AvatarImage src={ronroData?.picture || '/api/uploads/ronro_pics/ronro_main.jpg'} />
                  <AvatarFallback className="bg-emerald-700 text-white font-bold">R</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-white font-bold text-sm">RonRo</h3>
                  <p className="text-emerald-100 text-xs">Golf Assistant</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleMinimize}
                  className="text-white/70 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-lg"
                  title="Minimize"
                  data-testid="ronro-minimize-btn"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleClose}
                  className="text-white/70 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-lg"
                  title="Close"
                  data-testid="ronro-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Messages Area */}
            <div 
              className="p-3 overflow-y-auto bg-[var(--ps-bg-elevated)]"
              style={{ height: '280px' }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <Avatar className="w-16 h-16 mb-3">
                    <AvatarImage src={ronroData?.picture || '/api/uploads/ronro_pics/ronro_main.jpg'} />
                    <AvatarFallback className="bg-emerald-600 text-white text-xl font-bold">R</AvatarFallback>
                  </Avatar>
                  <p className="text-[var(--ps-text-primary)] font-medium text-sm">Hi! I&apos;m RonRo 👋</p>
                  <p className="text-[var(--ps-text-muted)] text-xs mt-1">
                    Ask me anything about golf rules or Par Seeker!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${isRonroMessage(msg) ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                          isRonroMessage(msg)
                            ? 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)] rounded-bl-md'
                            : 'bg-emerald-600 text-white rounded-br-md'
                        } ${msg.is_temp ? 'opacity-70' : ''}`}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {isSending && (
                    <div className="flex justify-start">
                      <div className="bg-[var(--ps-bg-surface)] px-4 py-2 rounded-2xl rounded-bl-md">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
            
            {/* Input Area */}
            <form onSubmit={handleSendMessage} className="p-3 border-t border-[var(--ps-border)] bg-[var(--ps-bg-surface)]">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Ask RonRo..."
                  className="flex-1 bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-xl px-3 py-2 text-sm text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)] focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  disabled={isSending || isLoading}
                  data-testid="ronro-message-input"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || isSending || isLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white p-2.5 rounded-xl transition-colors"
                  data-testid="ronro-send-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Intro Card (before chat opens) */}
        {isExpanded && !isChatOpen && (
          <div 
            className="bg-gradient-to-br from-[var(--ps-bg-surface)] to-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-2xl shadow-xl overflow-hidden animate-scale-in"
            style={{ width: '280px' }}
            data-testid="ronro-help-expanded"
          >
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="w-10 h-10 ring-2 ring-white/30">
                  <AvatarImage src={ronroData?.picture || '/api/uploads/ronro_pics/ronro_main.jpg'} />
                  <AvatarFallback className="bg-emerald-700 text-white font-bold">R</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-white font-bold text-sm">RonRo</h3>
                  <p className="text-emerald-100 text-xs">Your Golf Assistant</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-white/70 hover:text-white transition-colors p-1"
                data-testid="ronro-help-collapse"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-[var(--ps-text-secondary)] text-sm mb-3">
                Hi! 👋 I can help you with:
              </p>
              <ul className="text-xs text-[var(--ps-text-muted)] space-y-1.5 mb-4">
                <li className="flex items-center gap-2">
                  <span className="text-emerald-500">•</span> Golf Rules &amp; Etiquette
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-500">•</span> Scoring &amp; Handicaps
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-500">•</span> Par Seeker Features
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-emerald-500">•</span> GPS Yardages
                </li>
              </ul>
              
              <div className="space-y-2">
                {/* Yardage Button - shows when in active round */}
                {activeRound?.course_id && (
                  <button
                    onClick={openYardageMode}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                    data-testid="ronro-yardage-btn"
                  >
                    <Navigation className="w-4 h-4" />
                    Get Yardage
                  </button>
                )}
                <button
                  onClick={handleOpenChat}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                  data-testid="ronro-chat-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  Start Chat
                </button>
                <button
                  onClick={handleDismiss}
                  className="w-full text-[var(--ps-text-muted)] text-xs py-1.5 hover:text-[var(--ps-text-secondary)] transition-colors"
                  data-testid="ronro-dismiss-btn"
                >
                  Don&apos;t show again
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Yardage Mode Window */}
        {isYardageMode && (
          <div 
            className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
            style={{ width: '300px' }}
            data-testid="ronro-yardage-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                  <Navigation className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">Yardage</h3>
                  <p className="text-blue-100 text-xs">{activeRound?.course_name || 'GPS Mode'}</p>
                </div>
              </div>
              <button
                onClick={() => setIsYardageMode(false)}
                className="text-white/70 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Yardage Display */}
            <div className="p-4">
              {isGettingYardage ? (
                <div className="flex flex-col items-center justify-center py-8">
                  {/* Animated GPS Locator */}
                  <div className="relative mb-4">
                    {/* Pulsing rings */}
                    <div className="absolute inset-0 w-16 h-16 rounded-full bg-blue-500/20 animate-ping" />
                    <div className="absolute inset-2 w-12 h-12 rounded-full bg-blue-500/30 animate-ping" style={{ animationDelay: '0.2s' }} />
                    {/* Center icon with spin */}
                    <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                      <Navigation className="w-7 h-7 text-white animate-pulse" />
                    </div>
                  </div>
                  <p className="text-[var(--ps-text-primary)] font-medium mb-1 animate-pulse">
                    {locationPermission === 'granted' 
                      ? 'Getting Your Position...' 
                      : 'Requesting Location...'}
                  </p>
                  <p className="text-[var(--ps-text-muted)] text-xs text-center">
                    {locationPermission === 'granted'
                      ? 'Using GPS for accurate yardage'
                      : 'Check for a browser popup'}
                  </p>
                  {locationPermission !== 'granted' && (
                    <p className="text-amber-500 text-xs mt-2 text-center">
                      Tap Allow when prompted
                    </p>
                  )}
                </div>
              ) : gpsError ? (
                <div className="text-center py-6">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${
                    locationPermission === 'denied' ? 'bg-red-500/20' : 'bg-amber-500/20'
                  }`}>
                    {locationPermission === 'denied' ? (
                      <MapPin className="w-7 h-7 text-red-500" />
                    ) : (
                      <Navigation className="w-7 h-7 text-amber-500" />
                    )}
                  </div>
                  <p className="text-[var(--ps-text-primary)] font-medium mb-1">
                    {locationPermission === 'denied' ? 'Location Access Needed' : 'Location Error'}
                  </p>
                  <p className="text-[var(--ps-text-muted)] text-sm mb-4">
                    {gpsError}
                  </p>
                  
                  {/* Help text for enabling location */}
                  <div className="text-left bg-[var(--ps-bg-elevated)] rounded-lg p-3 mb-4 text-xs">
                    <p className="font-medium text-[var(--ps-text-primary)] mb-2">To enable location:</p>
                    <p className="text-[var(--ps-text-muted)] mb-1">
                      <strong>iPhone:</strong> Settings → Safari → Location → Allow
                    </p>
                    <p className="text-[var(--ps-text-muted)] mb-1">
                      <strong>Android:</strong> Tap 🔒 in address bar → Permissions
                    </p>
                    <p className="text-[var(--ps-text-muted)]">
                      <strong>Desktop:</strong> Click 🔒 icon near URL
                    </p>
                  </div>
                  
                  <button
                    onClick={getYardage}
                    disabled={isGettingYardage}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2"
                  >
                    {isGettingYardage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Navigation className="w-4 h-4" />
                        Try Again
                      </>
                    )}
                  </button>
                </div>
              ) : yardageData?.success ? (
                <div className="text-center">
                  {/* Main Yardage Display */}
                  <div className="mb-4">
                    <p className="text-[var(--ps-text-muted)] text-sm mb-1">
                      Hole {yardageData.hole}
                    </p>
                    <p className="text-5xl font-bold text-[var(--ps-text-primary)]">
                      {yardageData.yardage}
                    </p>
                    <p className="text-[var(--ps-text-muted)] text-sm">
                      {yardageData.estimate_type === 'gps' ? 'yards to center' : 'yards (total hole)'}
                    </p>
                    {/* Show note for estimated yardages */}
                    {yardageData.note && (
                      <p className="text-xs text-[var(--ps-text-muted)] mt-1">
                        {yardageData.note}
                      </p>
                    )}
                    {/* Show method indicator */}
                    {yardageData.method === 'tee_subtraction' && yardageData.traveled > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        📍 {yardageData.traveled}yd from tee
                      </p>
                    )}
                  </div>
                  
                  {/* Quick Adjust Buttons */}
                  {yardageData.nearby_holes && yardageData.nearby_holes.length > 1 && (
                    <div className="mb-4">
                      <p className="text-[var(--ps-text-muted)] text-xs mb-2">Wrong hole? Tap to adjust:</p>
                      <div className="flex items-center justify-center gap-2">
                        {yardageData.nearby_holes.map(hole => (
                          <button
                            key={hole}
                            onClick={() => hole !== yardageData.hole && handleHoleCorrection(hole)}
                            className={`w-10 h-10 rounded-xl font-bold text-sm transition-colors ${
                              hole === yardageData.hole
                                ? 'bg-blue-600 text-white'
                                : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] hover:bg-blue-500/20'
                            }`}
                            data-testid={`hole-btn-${hole}`}
                          >
                            {hole}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Refresh Button */}
                  <button
                    onClick={getYardage}
                    disabled={isGettingYardage}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                    data-testid="refresh-yardage-btn"
                  >
                    {isGettingYardage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Navigation className="w-4 h-4" />
                        Refresh Yardage
                      </>
                    )}
                  </button>
                  
                  {/* Data Quality Indicator */}
                  {yardageData.data_quality && (
                    <p className="text-[var(--ps-text-muted)] text-xs mt-3">
                      {yardageData.data_quality === 'learning' && '📡 Learning mode'}
                      {yardageData.data_quality === 'calculated' && '📍 Calculated from tee'}
                      {yardageData.data_quality === 'rough_estimate' && '📍 Estimated'}
                      {yardageData.data_quality === 'good' && '✓ GPS accurate'}
                      {yardageData.data_quality === 'excellent' && '✓✓ GPS precise'}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MapPin className="w-7 h-7 text-blue-500" />
                  </div>
                  <p className="text-[var(--ps-text-primary)] font-medium mb-1">
                    {yardageData?.error === 'No GPS data for this course yet' 
                      ? 'Building Course Data' 
                      : (yardageData?.message || 'No yardage data yet')}
                  </p>
                  <p className="text-[var(--ps-text-muted)] text-sm mb-4">
                    {yardageData?.error === 'No GPS data for this course yet'
                      ? 'RonRo learns green locations as you play. Finish a few holes to enable yardages!'
                      : 'Play a round to help build GPS data for this course'}
                  </p>
                  <button
                    onClick={getYardage}
                    disabled={isGettingYardage}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-xl text-sm flex items-center justify-center gap-2 mx-auto"
                  >
                    {isGettingYardage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'Try Again'
                    )}
                  </button>
                </div>
              )}
              
              {/* Switch to Chat */}
              <button
                onClick={() => { setIsYardageMode(false); handleOpenChat(); }}
                className="w-full mt-3 text-[var(--ps-text-muted)] text-xs py-1.5 hover:text-[var(--ps-text-secondary)] transition-colors flex items-center justify-center gap-1"
              >
                <MessageCircle className="w-3 h-3" />
                Chat with RonRo
              </button>
            </div>
          </div>
        )}
        
        {/* Collapsed Button */}
        {!isExpanded && !isChatOpen && !isYardageMode && (
          <button
            onClick={handleButtonClick}
            className="relative group"
            data-testid="ronro-help-btn"
          >
            <div className="absolute inset-0 bg-emerald-500 rounded-full blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
            <div className="relative w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95">
              <Avatar className="w-11 h-11">
                <AvatarImage src={ronroData?.picture || '/api/uploads/ronro_pics/ronro_main.jpg'} />
                <AvatarFallback className="bg-emerald-700 text-white font-bold text-lg">R</AvatarFallback>
              </Avatar>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--ps-gold)] rounded-full flex items-center justify-center shadow">
                <HelpCircle className="w-3 h-3 text-white" />
              </div>
            </div>
          </button>
        )}
        
        {/* Minimized indicator when chat was open */}
        {!isExpanded && !isChatOpen && !isYardageMode && messages.length > 0 && (
          <div className="absolute -top-1 -left-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
            <span className="text-white text-[10px] font-bold">{messages.length > 9 ? '9+' : messages.length}</span>
          </div>
        )}
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes scale-in {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

/**
 * Helper function to re-enable RonRo help button
 */
export const enableRonRoHelpButton = () => {
  localStorage.removeItem('ronro_help_hidden');
  window.location.reload();
};

/**
 * Check if RonRo help button is hidden
 */
export const isRonRoHelpHidden = () => {
  return localStorage.getItem('ronro_help_hidden') === 'true';
};

/**
 * Set active round for RonRo yardage mode
 * Call this when user starts a scoring session or game
 * @param {Object} roundData - { course_id, course_name, current_hole, started_at }
 */
export const setActiveRoundForYardage = (roundData) => {
  if (roundData) {
    localStorage.setItem('par_seeker_active_round', JSON.stringify({
      ...roundData,
      started_at: roundData.started_at || new Date().toISOString()
    }));
    // Trigger storage event for same-tab listeners
    window.dispatchEvent(new Event('storage'));
  } else {
    localStorage.removeItem('par_seeker_active_round');
  }
};

/**
 * Clear active round (call when round is completed)
 */
export const clearActiveRound = () => {
  localStorage.removeItem('par_seeker_active_round');
  window.dispatchEvent(new Event('storage'));
};

/**
 * Update current hole in active round
 */
export const updateActiveRoundHole = (holeNumber) => {
  const stored = localStorage.getItem('par_seeker_active_round');
  if (stored) {
    try {
      const round = JSON.parse(stored);
      round.current_hole = holeNumber;
      localStorage.setItem('par_seeker_active_round', JSON.stringify(round));
    } catch (e) {
      console.log('Failed to update hole');
    }
  }
};

export default RonRoHelpButton;
